import { type RefObject, useEffect, useState } from 'react';

const PARALLAX_RANGE_VH = 1;
const MAX_TRANSLATE_PERCENT = 8;

/**
 * Scroll-based vertical parallax for full-viewport hero media.
 * Returns a percentage translate suitable for `transform: translateY(...)`.
 */
export const useParallax = () => {
  const [translateY, setTranslateY] = useState(0);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setEnabled(!mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setTranslateY(0);
      return;
    }

    let frame = 0;

    const update = () => {
      const progress = Math.min(
        window.scrollY / (window.innerHeight * PARALLAX_RANGE_VH),
        1,
      );
      setTranslateY(-progress * MAX_TRANSLATE_PERCENT);
    };

    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [enabled]);

  return translateY;
};

/**
 * Pins the mountains to the viewport (Figma Make parallax): the full graphic
 * stays on screen while the 240k headline and cards scroll over it.
 *
 * @param ref - Testimonials section
 * @returns translateY in pixels
 */
export const useElementParallax = (ref: RefObject<HTMLElement | null>) => {
  const [translateY, setTranslateY] = useState(0);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setEnabled(!mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setTranslateY(0);
      return;
    }

    let frame = 0;

    const update = () => {
      const el = ref.current;
      if (!el) {
        return;
      }

      const rect = el.getBoundingClientRect();
      const viewH = window.innerHeight;
      // Keep the mountain’s baseline on the viewport bottom (clipped by section).
      setTranslateY(viewH - rect.bottom);
    };

    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [enabled, ref]);

  return translateY;
};
